import React, { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Desktop from './Desktop';
import '../styles/Intro.css';

gsap.registerPlugin(ScrollTrigger);

const FRAME_COUNT = 301;
const START_FRAME = 19; // 0-indexed, i.e. frame_0020.webp
const FRAME_PATH = '/frames/hero/frame_';

const Intro = () => {
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const introTextRef = useRef(null);
  const aboutCardRef = useRef(null);
  const desktopPlaneRef = useRef(null);
  const loaderRef = useRef(null);
  const loaderFillRef = useRef(null);
  const loaderPctRef = useRef(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const frameCanvas = canvasRef.current;
    const canvasWrap = canvasWrapRef.current;
    const introText = introTextRef.current;
    const aboutCard = aboutCardRef.current;
    const desktopPlane = desktopPlaneRef.current;
    const loader = loaderRef.current;

    if (!frameCanvas || !section) return;

    const ctx = frameCanvas.getContext('2d');

    // ── Preload all frames into memory ──
    const srcs = [];
    for (let i = 1; i <= FRAME_COUNT; i++) {
      srcs.push(`${FRAME_PATH}${String(i).padStart(4, '0')}.webp`);
    }

    const preloaded = new Array(FRAME_COUNT);
    let loadedCount = 0;
    let currentFrame = -1;
    let started = false;

    const resizeCanvas = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      // Use clientWidth/Height — getBoundingClientRect reflects GSAP
      // transforms (scale), which would distort the canvas backing size.
      const cw = frameCanvas.clientWidth || frameCanvas.parentElement?.clientWidth || 1;
      const ch = frameCanvas.clientHeight || frameCanvas.parentElement?.clientHeight || 1;
      const w = Math.max(1, Math.round(cw * dpr));
      const h = Math.max(1, Math.round(ch * dpr));
      if (frameCanvas.width !== w || frameCanvas.height !== h) {
        frameCanvas.width = w;
        frameCanvas.height = h;
      }
    };

    const drawFitted = (img) => {
      const cw = frameCanvas.width;
      const ch = frameCanvas.height;
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      if (!iw || !ih || !cw || !ch) return;
      ctx.clearRect(0, 0, cw, ch);
      const canvasAspect = cw / ch;
      const imgAspect = iw / ih;
      // Use cover when aspects are similar (desktop / large screens with the
      // square source); use contain when aspects diverge a lot (portrait
      // phones/tablets) so the full binocular is visible.
      const aspectRatio = canvasAspect / imgAspect;
      const useContain = aspectRatio < 0.65 || aspectRatio > 1.55;
      const scale = useContain
        ? Math.min(cw / iw, ch / ih)
        : Math.max(cw / iw, ch / ih);
      const dw = iw * scale;
      const dh = ih * scale;
      const dx = (cw - dw) / 2;
      const dy = (ch - dh) / 2;
      ctx.drawImage(img, dx, dy, dw, dh);
    };

    const showFrame = (index) => {
      const idx = Math.min(Math.max(index, 0), FRAME_COUNT - 1);
      if (idx === currentFrame) return;
      const img = preloaded[idx];
      if (!img || !img.complete || !img.naturalWidth) return;
      currentFrame = idx;
      resizeCanvas();
      drawFitted(img);
    };

    const onResize = () => {
      if (currentFrame >= 0) {
        resizeCanvas();
        const img = preloaded[currentFrame];
        if (img) drawFitted(img);
      }
    };
    window.addEventListener('resize', onResize);

    const consumeNavIntent = () => {
      let raw;
      try { raw = sessionStorage.getItem('navIntent'); } catch (_) { raw = null; }
      if (!raw) return;
      sessionStorage.removeItem('navIntent');
      let intent;
      try { intent = JSON.parse(raw); } catch (_) { return; }
      if (!intent || !intent.type) return;

      // Use the actual document height (ScrollTrigger pin has already
      // extended the page) rather than guessing from intro.offsetTop.
      const docEl = document.documentElement;
      const maxScroll = () => Math.max(0, docEl.scrollHeight - window.innerHeight);

      // Safari sometimes silently overrides programmatic scroll right after
      // a route change (scroll restoration / layout settling). Retry the
      // scroll several times until it sticks.
      const persistentScroll = (target) => {
        let attempts = 0;
        const tick = () => {
          attempts += 1;
          window.scroll(0, target());
          if (Math.abs(window.scrollY - target()) > 5 && attempts < 20) {
            setTimeout(tick, 60);
          }
        };
        tick();
      };

      if (intent.type === 'about') {
        // About card sits ~35% through the Intro pinned timeline.
        persistentScroll(() => maxScroll() * 0.35);
      } else if (intent.type === 'openApp' && intent.appId) {
        // Jump instantly to the bottom (desktop view).
        persistentScroll(() => maxScroll());
        // Poll for the desktop API (it becomes available once Desktop mounts)
        // and trigger the open once we're near the bottom.
        let tries = 0;
        const tryOpen = () => {
          tries += 1;
          const atDesktop = window.scrollY >= maxScroll() - 10;
          if (window.__desktopOpenWindow && atDesktop) {
            window.__desktopOpenWindow(intent.appId);
            return;
          }
          if (tries > 80) {
            if (window.__desktopOpenWindow) window.__desktopOpenWindow(intent.appId);
            return;
          }
          setTimeout(tryOpen, 100);
        };
        setTimeout(tryOpen, 250);
      }
    };

    const finishLoading = () => {
      if (started) return;
      started = true;
      showFrame(START_FRAME);
      if (loader) loader.style.display = 'none';
      // Refresh ScrollTrigger now that the section is fully laid out, then
      // run any pending nav intent (e.g. user clicked Projects from /blogs).
      requestAnimationFrame(() => {
        ScrollTrigger.refresh();
        consumeNavIntent();
      });
    };

    const onFrameLoad = () => {
      loadedCount++;
      const pct = Math.round((loadedCount / FRAME_COUNT) * 100);
      if (loaderFillRef.current) loaderFillRef.current.style.width = pct + '%';
      if (loaderPctRef.current) loaderPctRef.current.textContent = pct + '%';
      if (loadedCount >= FRAME_COUNT) finishLoading();
    };

    // Concurrency-limited loader. Safari throttles >6 connections per host;
    // exceeding that just queues requests and starves the rest of the page.
    // Decoding all 301 WebP frames in parallel was the source of the Safari crash.
    const CONCURRENCY = 6;
    let nextIndex = 0;

    // Order the load queue around START_FRAME so the first visible frame
    // and its neighbours are decoded earliest.
    const loadOrder = [];
    loadOrder.push(START_FRAME);
    for (let offset = 1; offset < FRAME_COUNT; offset++) {
      const a = START_FRAME + offset;
      const b = START_FRAME - offset;
      if (a < FRAME_COUNT) loadOrder.push(a);
      if (b >= 0) loadOrder.push(b);
    }

    // Tiny offscreen canvas used to force a real decode of each frame.
    // Safari's img.decode() is unreliable (often resolves without decoding),
    // but a drawImage() into a canvas forces a synchronous decode in every
    // browser. We draw at 1×1 — the goal is just to materialise the bitmap;
    // the real rendering happens on the visible canvas later.
    const warmupCanvas = document.createElement('canvas');
    warmupCanvas.width = 1;
    warmupCanvas.height = 1;
    const warmupCtx = warmupCanvas.getContext('2d');

    const loadOne = () => {
      if (nextIndex >= loadOrder.length) return;
      const i = loadOrder[nextIndex++];
      const img = new Image();
      img.decoding = 'async';
      const finish = () => {
        onFrameLoad();
        loadOne();
      };
      img.onload = () => {
        // Force a true decode by drawing once. This blocks the main thread
        // briefly per frame but bounded concurrency (6) keeps it smooth, and
        // it's far better than blocking for 10s during the first scroll.
        try {
          warmupCtx.drawImage(img, 0, 0, 1, 1);
        } catch (_) { /* ignore */ }
        finish();
      };
      img.onerror = finish;
      img.src = srcs[i];
      preloaded[i] = img;
    };

    // Defer to a macrotask so the GSAP/ScrollTrigger setup below runs first.
    // Safari fires onload synchronously for cached images, which can otherwise
    // race finishLoading() ahead of ScrollTrigger initialization, leaving the
    // page with no pin-extended height (no scrollable area).
    let loaderStarted = false;
    const startLoader = () => {
      if (loaderStarted) return;
      loaderStarted = true;
      for (let k = 0; k < CONCURRENCY; k++) loadOne();
    };

    // ── GSAP — set up immediately, section is always in DOM ──
    const scrollEnd = '+=1000%';

    const gsapCtx = gsap.context(() => {  // no scope — plane is outside section
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: scrollEnd,
          pin: true,
          pinSpacing: true,
          scrub: 0.3,
          onUpdate: (self) => {
            const dt = document.getElementById('desktop');
            if (!dt || !desktopPlane) return;
            // When plane is full-screen (progress >= 0.97), clear transforms
            if (self.progress >= 0.97) {
              if (dt.classList.contains('in-plane')) {
                dt.classList.remove('in-plane');
                desktopPlane.style.transform = 'none';
                desktopPlane.style.filter = 'none';
                desktopPlane.style.borderRadius = '0';
                desktopPlane.style.border = 'none';
                desktopPlane.style.boxShadow = 'none';
                desktopPlane.style.pointerEvents = 'auto';
              }
            } else {
              if (!dt.classList.contains('in-plane')) {
                dt.classList.add('in-plane');
                // Remove our manual overrides so GSAP can control again
                desktopPlane.style.transform = '';
                desktopPlane.style.filter = '';
                desktopPlane.style.borderRadius = '';
                desktopPlane.style.border = '';
                desktopPlane.style.boxShadow = '';
                desktopPlane.style.pointerEvents = '';
              }
            }
          },
        },
      });

      // ── Intro text slides out ──
      tl.to(introText, {
        x: '-100%',
        opacity: 0,
        duration: 0.06,
        ease: 'power2.in',
      }, 0.08);

      // ── Canvas wrapper: zoomed out + 3D tilt → fullscreen flat ──
      gsap.set(canvasWrap, { rotateY: -6, rotateX: 2, perspective: 1200, scale: 0.85 });
      tl.to(canvasWrap, {
        left: '0%',
        width: '100%',
        rotateY: 0,
        rotateX: 0,
        scale: 1,
        borderRadius: '0px',
        duration: 0.14,
        ease: 'power2.inOut',
      }, 0.08);

      // ── Frame scrubbing — uniform speed, frames 20→300 ──
      const frameObj = { frame: START_FRAME };

      const doRender = () => {
        showFrame(Math.round(frameObj.frame));
      };

      // ── Frame sequence (takes up first 70% of timeline) ──
tl.to(frameObj, {
  frame: FRAME_COUNT - 1,
  snap: 'frame',
  ease: 'none',
  duration: 0.70,
  onUpdate: doRender,
}, 0);

// ── About card (0.30→0.67) — given more time on screen ──
gsap.set(aboutCard, { xPercent: -50, yPercent: -50 });
tl.fromTo(aboutCard,
  { opacity: 0, y: 60, scale: 0.95 },
  { opacity: 1, y: 0, scale: 1, duration: 0.08 },
  0.30
);
tl.to(aboutCard, { opacity: 0, y: -40, duration: 0.06 }, 0.67);

// ── Desktop plane: appears after About, zooms from distance to fullscreen ──
if (desktopPlane) {
  gsap.set(desktopPlane, {
    transformPerspective: 1200,
    scale: 0.22,
    xPercent: 25,
    yPercent: -18,
    rotateY: -14,
    rotateX: 8,
    opacity: 0,
    filter: 'blur(6px) brightness(0.4)',
  });

  // Fade in the plane
  tl.to(desktopPlane, {
    opacity: 1,
    duration: 0.04,
    ease: 'power1.in',
  }, 0.72);

  // Zoom + straighten + sharpen (0.76 → 0.96)
  tl.to(desktopPlane, {
    scale: 1,
    xPercent: 0,
    yPercent: 0,
    rotateY: 0,
    rotateX: 0,
    filter: 'blur(0px) brightness(1)',
    borderRadius: '0px',
    border: '0px solid transparent',
    boxShadow: 'none',
    duration: 0.24,
    ease: 'power2.inOut',
  }, 0.76);

  // Enable pointer events once plane is nearly full-screen
  tl.set(desktopPlane, { pointerEvents: 'auto' }, 0.94);
}

    });

    // GSAP + ScrollTrigger are now initialized. Refresh once so the
    // pinSpacer + scrollHeight are correct, then start the frame loader.
    ScrollTrigger.refresh();
    startLoader();

    return () => {
      window.removeEventListener('resize', onResize);
      gsapCtx.revert();
    };
  }, []);

  return (
    <>
      {/* ── Loading overlay — hidden via DOM when done ── */}
      <div className="hero-loader" ref={loaderRef}>
        <div className="loader-inner">
          <p className="loader-brand">Exploring Curiosity</p>
          <div className="loader-bar-track">
            <div className="loader-bar-fill" ref={loaderFillRef} />
          </div>
          <p className="loader-pct" ref={loaderPctRef}>0%</p>
        </div>
      </div>

      <section id="intro" className="hero" ref={sectionRef}>

        {/* ── Intro text (left side) ── */}
        <div className="hero-intro-text" ref={introTextRef}>
          <img src="/profilePic.png" alt="Sudharshan Ramesh" className="intro-dp" />
          <p className="intro-greeting">Hi, I'm</p>
          <h1 className="intro-name">
            Sudharshan <span className="intro-name-accent">Ramesh</span>
          </h1>
          <h2 className="intro-role">Machine Learning Engineer<br />& Software Developer</h2>
          <p className="intro-tagline">
            I design and ship end-to-end systems, from applied ML
            and MLOps to resilient APIs and refined interfaces.
          </p>
        </div>

        {/* ── 3D Canvas (right side → fullscreen) ── */}
        <div className="hero-canvas-wrap" ref={canvasWrapRef}>
          <canvas ref={canvasRef} className="hero-canvas" />

          {/* About card — no photo */}
          <div className="barrel-card about-card" ref={aboutCardRef}>
            <p className="card-label">About</p>
            <h2 className="about-card-title">
              Exploring curiosity through ML &amp; software systems.
            </h2>
            <p className="about-card-text">
              I build production-grade models, resilient services and
              elegant product experiences, across the full lifecycle from
              research to deployment, observability and user-facing design.
            </p>
          </div>

        </div>

      </section>

      {/* ── Desktop plane: position:fixed overlay animated by Intro timeline ── */}
      <div className="desktop-preview-plane" ref={desktopPlaneRef}>
        <Desktop />
      </div>
    </>
  );
};

export default Intro;